<template>
  <div v-if="editor">
    <div class="text-editor-wrapper">
      <div class="actions" @mousedown="onFormattingMouseDown">
        <!-- tabindex is needed to tell the browser that these elements are interactive. fills focus/blur events. -->
        <div
          v-if="showAction(Action.FILE)"
          @click="openFileDialog"
          class="is-clickable black-hover icon-size"
          tabindex="0"
        >
          <n-icon size="s" icon="document-add" />
        </div>
        <div class="divider" />
        <div
          v-if="showAction(Action.BOLD)"
          @click="editor.chain().focus().toggleBold().run()"
          class="right-spacing black-hover button-size"
          tabindex="1"
        >
          <b :class="{ active: editor.isActive('bold') }">B</b>
        </div>
        <div
          v-if="showAction(Action.ITALIC)"
          @click="editor.chain().focus().toggleItalic().run()"
          class="black-hover button-size"
          tabindex="2"
        >
          <i :class="{ active: editor.isActive('italic') }">i</i>
        </div>
        <div
          v-if="showAction(Action.UNDERLINED)"
          @click="editor.chain().focus().toggleUnderline().run()"
          class="right-spacing black-hover button-size"
          tabindex="3"
        >
          <u :class="{ active: editor.isActive('underline') }">U</u>
        </div>

        <div
          v-if="showAction(Action.STRIKE)"
          @click="editor.chain().focus().toggleStrike().run()"
          class="right-spacing black-hover button-size"
          tabindex="4"
        >
          <s :class="{ active: editor.isActive('strike') }">S</s>
        </div>

        <div
          v-if="showAction(Action.LINK)"
          @click="openLinkPopup()"
          class="black-hover icon-size"
          ref="linkbutton"
          tabindex="5"
        >
          <n-icon icon="at-symbol" size="s" :class="{ active: editor.isActive('link') }" />
        </div>
      </div>
      <div
        v-if="showSend"
        class="send-button"
        :class="{ active: canSend, 'is-clickable': canSend, 'black-hover': canSend }"
        @click="canSend && $emit('send')"
        tabindex="6"
      >
        <n-icon icon="paper-airplane" rotate="1" />
      </div>
    </div>
    <LinkInput :link="linkValue" @updateLink="updateLink" @removeLink="removeLink" ref="linkInput" />
  </div>
</template>

<script lang="ts">
import { Editor } from '@tiptap/core'
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import LinkInput from './LinkInput.vue'
import { normalizeLinkHref, selectedLinkHref } from '@/util/link-href'

export enum Action {
  FILE,
  BOLD,
  ITALIC,
  UNDERLINED,
  LINK,
  STRIKE,
  BLOCKQUOTE
}

@Component({
  components: {
    LinkInput
  }
})
export default class TextEditorActions extends Vue {
  @Prop() private editor!: Editor
  @Prop({ default: false }) private showSend!: boolean
  @Prop({ default: () => [] }) private hideActions!: Action[]
  @Prop({ default: false }) private filesReady!: boolean
  Action = Action

  private linkValue = ''

  @Ref('linkInput') inputRef!: LinkInput

  get canSend(): boolean {
    return this.editor?.getText().trim().length > 0 || this.filesReady
  }

  showAction(action: Action): boolean {
    return !this.hideActions.includes(action)
  }

  onFormattingMouseDown(event: MouseEvent) {
    event.preventDefault()
  }

  openLinkPopup() {
    this.linkValue = selectedLinkHref(this.editor)

    const linkbuttonRef = this.$refs.linkbutton as any
    this.inputRef.open(linkbuttonRef)
  }

  updateLink(link: string) {
    if (!link) {
      this.removeLink()
      return
    }
    link = normalizeLinkHref(link)
    this.editor.chain().focus().extendMarkRange('link').setLink({ href: link }).run()
    this.inputRef.close()
  }

  removeLink() {
    this.editor.chain().focus().unsetLink().run()
    this.inputRef.close()
  }

  openFileDialog() {
    this.$emit('openFileDialog')
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles' as *;

.text-editor-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: $notch-color-neutral-grey-500;
}

.actions {
  display: flex;
  align-items: center;
}

.button-size {
  display: flex;
  align-items: center;
  cursor: pointer;
  width: $notch-space-5x;
}

.divider {
  height: $notch-space-6x;
  width: $notch-space-05x;
  background-color: $notch-color-neutral-sand-200;
  margin: 0 $notch-space-3x;
}

.icon-size {
  display: flex;
  cursor: pointer;
  height: $notch-space-5x;
}

.send-button {
  display: flex;
  align-items: center;
}

.right-spacing {
  margin-right: $notch-space-1x;
}

.active {
  color: $notch-color-neutral-grey-900;
}
</style>
